// Minimal Markdown renderer for recap / solution prose. Dependency-free (the
// build runs server-side without local Node, and we avoid adding react-markdown
// for now). Handles headings, bullet/numbered lists, fenced code blocks, and
// passes inline text through MathText so `$...$` / `$$...$$` LaTeX renders via
// KaTeX. A richer pipeline (react-markdown + rehype-katex + shiki) is a drop-in
// upgrade later if needed.
import { Fragment, type ReactNode } from "react";
import { MathText } from "@/components/math-text";

export function CodeBlock({ code, language }: { code: string; language?: string }) {
  return (
    <div className="my-3 overflow-hidden rounded-lg border border-black/10 bg-zinc-900">
      {language && (
        <div className="border-b border-white/10 px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-zinc-400">
          {language}
        </div>
      )}
      <pre className="overflow-x-auto p-3 text-xs leading-relaxed text-zinc-100">
        <code>{code}</code>
      </pre>
    </div>
  );
}

export function MarkdownLite({ text }: { text: string }) {
  if (!text) return null;
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const blocks: ReactNode[] = [];
  let i = 0;
  let listBuf: { ordered: boolean; items: string[] } | null = null;

  const flushList = () => {
    if (!listBuf) return;
    const { ordered, items } = listBuf;
    const List = ordered ? "ol" : "ul";
    blocks.push(
      <List key={`l-${blocks.length}`} className={ordered ? "my-2 list-decimal pl-6" : "my-2 list-disc pl-6"}>
        {items.map((it, k) => (
          <li key={k} className="my-0.5">
            <MathText text={it} />
          </li>
        ))}
      </List>,
    );
    listBuf = null;
  };

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block.
    const fence = line.match(/^```(\w+)?\s*$/);
    if (fence) {
      flushList();
      const lang = fence[1];
      const buf: string[] = [];
      i++;
      while (i < lines.length && !/^```\s*$/.test(lines[i])) {
        buf.push(lines[i]);
        i++;
      }
      i++; // skip closing fence
      blocks.push(<CodeBlock key={`c-${blocks.length}`} code={buf.join("\n")} language={lang} />);
      continue;
    }

    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      flushList();
      const level = heading[1].length;
      const content = heading[2];
      const cls =
        level <= 1
          ? "mt-4 mb-2 text-xl font-bold text-ink"
          : level === 2
            ? "mt-4 mb-1.5 text-lg font-semibold text-ink"
            : "mt-3 mb-1 text-base font-semibold text-ink";
      blocks.push(
        <p key={`h-${blocks.length}`} className={cls}>
          <MathText text={content} />
        </p>,
      );
      i++;
      continue;
    }

    const bullet = line.match(/^\s*[-*]\s+(.*)$/);
    const numbered = line.match(/^\s*\d+\.\s+(.*)$/);
    if (bullet || numbered) {
      const ordered = !!numbered;
      const item = (bullet ? bullet[1] : numbered![1]).trim();
      if (!listBuf || listBuf.ordered !== ordered) {
        flushList();
        listBuf = { ordered, items: [] };
      }
      listBuf.items.push(item);
      i++;
      continue;
    }

    if (line.trim() === "") {
      flushList();
      i++;
      continue;
    }

    // Paragraph: gather consecutive non-empty, non-special lines.
    flushList();
    const para: string[] = [line];
    i++;
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !/^(#{1,4})\s/.test(lines[i]) &&
      !/^\s*[-*]\s/.test(lines[i]) &&
      !/^\s*\d+\.\s/.test(lines[i]) &&
      !/^```/.test(lines[i])
    ) {
      para.push(lines[i]);
      i++;
    }
    blocks.push(
      <p key={`p-${blocks.length}`} className="my-2 leading-relaxed text-ink-soft">
        <MathText text={para.join(" ")} />
      </p>,
    );
  }
  flushList();

  return <div className="text-sm">{blocks.map((b, k) => <Fragment key={k}>{b}</Fragment>)}</div>;
}
